import { rem } from 'polished'
import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type {
  CreateBusItemViewModel,
  CreateFerryItemViewModel,
  CreateOtherTransportItemViewModel,
  CreateTrainItemViewModel,
} from './models'
import type { CreateItemProps } from './utils'
import {
  createFerryPayloadFromViewModel,
  createOtherTransportPayloadFromViewModel,
  createTrainPayloadFromViewModel,
  createBusPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'
import TransportFields from '../Common/TransportFields'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineSubwayIcon from 'LuxKit/Icons/line/LineSubwayIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'
const ChipContainer = styled(Group)`
  margin-bottom: ${rem(16)};
  margin-top: ${rem(20)};
`

type CreateTransportItemViewModel =
  | CreateTrainItemViewModel
  | CreateBusItemViewModel
  | CreateOtherTransportItemViewModel
  | CreateFerryItemViewModel

const SELECT_OPTIONS: Array<{
  value: CreateTransportItemViewModel['type']
  label: string
  icon?: React.ReactElement
}> = [
  { value: 'OTHER_TRANSPORT', label: 'Private Transport' },
  { value: 'TRAIN', label: 'Train', icon: <LineSubwayIcon /> },
  { value: 'BUS', label: 'Bus', icon: <LineBusIcon /> },
  { value: 'FERRY', label: 'Ferry', icon: <LineShipIcon /> },
]

function CreateTransport(props: CreateItemProps) {
  const { tripId, defaultDate, rankSpec } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const methods = useForm<CreateTransportItemViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'OTHER_TRANSPORT',
      sourceType: 'NA',
      isBooked: false,
      isEndDateSame: true,
      startDate: defaultDate,
      endDate: defaultDate,
      startPlace: {},
      endPlace: {},
    },
  })
  const { handleSubmit, watch, setValue } = methods

  const type = watch('type')

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: (res) => {
      scrollToNewItemWithRetry(res.id)
      closeModal()
    },
  })

  const createTransportItem = useCallback(
    (viewModel: CreateTransportItemViewModel) => {
      switch (viewModel.type) {
        case 'TRAIN':
          createMutate({
            tripId,
            tripItem: createTrainPayloadFromViewModel(viewModel),
            rankSpec,
          })
          break
        case 'BUS':
          createMutate({
            tripId,
            tripItem: createBusPayloadFromViewModel(viewModel),
            rankSpec,
          })
          break
        case 'OTHER_TRANSPORT':
          createMutate({
            tripId,
            tripItem: createOtherTransportPayloadFromViewModel(viewModel),
            rankSpec,
          })
          break
        case 'FERRY':
          createMutate({
            tripId,
            tripItem: createFerryPayloadFromViewModel(viewModel),
            rankSpec,
          })
          break
      }
    },
    [createMutate, tripId, rankSpec],
  )

  const modalTitle = intl.formatMessage({
    id: '0lTmr6aGNhmteHapjWDo',
    defaultMessage: 'Add transport',
    description: 'Modal title when adding transport to trip in the trip planner',
  })
  const saveLabel = intl.formatMessage({
    id: '1RvzgmrwhusOfz5XyUrj',
    defaultMessage: 'Save',
    description: 'Save button label in the add transport modal dialog window',
  })
  const chipLabels: Record<CreateTransportItemViewModel['type'], string> = {
    OTHER_TRANSPORT: intl.formatMessage({
      id: 'ybBJTFsreoOW742QRqoL',
      defaultMessage: 'Private Transport',
      description: 'Filter chip label for private transport type in the filters panel',
    }),
    TRAIN: intl.formatMessage({
      id: 'AuGTOaf3wHuNGpYNQHXl',
      defaultMessage: 'Train',
      description: 'Filter chip label for train transport type in the filters panel',
    }),
    BUS: intl.formatMessage({
      id: '-nsq5md1J60JkMJM96Cc',
      defaultMessage: 'Bus',
      description: 'Filter chip label for bus transport type in the filters panel',
    }),
    FERRY: intl.formatMessage({
      id: '7Z1UjDbd6Kw_aS4aBggy',
      defaultMessage: 'Ferry',
      description: 'Filter chip label for ferry transport type in the filters panel',
    }),
  }

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <ChipContainer
              direction="horizontal"
              verticalAlign="center"
              gap={8}
              wrap="wrap"
            >
              {SELECT_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  startIcon={option.icon}
                  onClick={() => setValue('type', option.value)}
                  selected={type === option.value}
                  size="medium"
                >
                  {chipLabels[option.value]}
                </FilterChip>
              ))}
            </ChipContainer>
            <TransportFields tripId={tripId} />
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createTransportItem),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateTransport
